export const ADMIN_SESSION_KEY = 'saloni_admin_session_token';

export const readStoredAdminSession = () => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(ADMIN_SESSION_KEY) || sessionStorage.getItem(ADMIN_SESSION_KEY) || '';
};

export function sanitizeSlug(input) {
  return String(input || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

/** URL key from display name; appends -2, -3… if taken. */
export function uniqueSlugFromName(displayName, takenSlugs = []) {
  const base = sanitizeSlug(displayName);
  if (!base) return '';
  const taken = new Set((takenSlugs || []).map((s) => String(s || '').trim().toLowerCase()).filter(Boolean));
  if (!taken.has(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base}-${n}`.slice(0, 64);
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}-${Date.now()}`.slice(0, 64);
}

const VOWELS = new Set(['a', 'e', 'i', 'o', 'u']);

function lettersOnlySlug(slug) {
  return String(sanitizeSlug(slug) || '')
    .replace(/-/g, '')
    .replace(/[^a-z]/g, '');
}

/** 5–6 letter code blended from category + subcategory (e.g. RAKHI + kids → RAKIDS). */
export function productIdTaxonomyToken(categorySlug, subcategorySlug) {
  const cat = lettersOnlySlug(categorySlug) || 'cat';
  let sub = lettersOnlySlug(subcategorySlug);
  if (!sub || sub === 'all') sub = '';

  const pick = (str, n) => {
    if (!str) return '';
    const consonants = [...str].filter((c) => !VOWELS.has(c)).join('');
    const src = consonants.length >= n ? consonants : str;
    return src.slice(0, n);
  };

  let tok = sub ? (pick(cat, 3) + pick(sub, 3)).toUpperCase() : pick(cat, 6).toUpperCase();
  if (tok.length < 5) tok = (tok + cat).toUpperCase();
  return tok.slice(0, 6);
}

/**
 * Auto product id: `SD-{taxonomy}-{4digits}` (e.g. `SD-RAKIDS-4821`).
 * Uniqueness is enforced against `existingIds` (all products, all categories).
 */
export function generateMeaningfulProductId(categorySlug, subcategorySlug, existingIds) {
  const existing = new Set(
    (existingIds || []).map((x) => String(x || '').trim().toLowerCase()).filter(Boolean)
  );
  const token = productIdTaxonomyToken(categorySlug, subcategorySlug);

  let seq = 0;
  let candidate = '';
  while (seq < 500) {
    let suffix;
    if (seq === 0) {
      suffix = String(Math.floor(1000 + Math.random() * 9000));
    } else {
      suffix = String(1000 + ((seq * 7919 + token.charCodeAt(0)) % 9000));
    }
    candidate = `SD-${token}-${suffix}`;
    if (!existing.has(candidate.toLowerCase())) break;
    seq += 1;
  }
  if (seq >= 500 || existing.has(candidate.toLowerCase())) {
    candidate = `SD-${token}-${Date.now().toString().slice(-4)}`;
  }
  if (existing.has(candidate.toLowerCase())) {
    candidate = `SD-${token}-${Date.now().toString(36).slice(-4).toUpperCase()}`;
  }
  return candidate.slice(0, 191);
}

/** Allow only a single decimal point while typing a money amount. */
export function normalizePriceTyping(raw) {
  let s = String(raw ?? '').replace(/[^\d.]/g, '');
  const dot = s.indexOf('.');
  if (dot !== -1) {
    s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '');
  }
  return s;
}

/** Parse price from text field; empty or invalid → 0 */
export function parsePriceText(raw) {
  const n = parseFloat(String(raw ?? '').trim());
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Internal path only (same origin). Rejects protocol-relative and /login loops. */
export function safeReturnPath(raw, fallback = '/') {
  const s = String(raw || '').trim();
  if (!s || s === '/login') return fallback;
  if (!s.startsWith('/') || s.startsWith('//')) return fallback;
  return s;
}

export const defaultProduct = {
  id: '',
  name: '',
  type: '',
  price: 0,
  sale_price: null,
  description: '',
  image: '',
  images: [],
  subcategory: '',
  video: '',
  videos: [],
  noSound: true,
  is_visible: true,
  stock_qty: null,
  char_personalization: false,
  char_personalization_letters: '',
  gift_tags: [],
};

/** @param {{ subcategories?: Array<{slug:string}>, types?: Array<{slug:string}> }} cat */
export function slugInList(list, value) {
  const v = String(value || '').trim();
  if (!v) return false;
  return (list || []).some((x) => x.slug === v);
}
